import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, MapPin, ShieldCheck, ShieldAlert, ShieldQuestion, Clock, HelpCircle,
  MoreVertical, CalendarClock, ClipboardCheck, EyeOff, Eye, Download, User, FileSpreadsheet,
  CalendarCog,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { getSystemTypeIcon } from '@/lib/systemTypeIcons';
import { tipoProximaLabel } from '@/lib/coberturaRevisiones';
import { useMantenimientosDashboard, type SistemaRevisionRow } from '@/hooks/useMantenimientosDashboard';
import { ProgramarRevisionDialog } from './ProgramarRevisionDialog';
import { FijarPeriodoRevisionDialog } from '@/components/sistemas/FijarPeriodoRevisionDialog';

const LS_ESTADO = 'mantenimientos.filtroEstado';
const LS_TIPO = 'mantenimientos.filtroTipo';
const LS_RANGO = 'mantenimientos.rangoDias';
const LS_AGRUPAR = 'mantenimientos.agrupar';

const ORDEN: Record<string, number> = {
  vencido: 0, requiere_aclaracion: 1, sin_iniciar: 2, proximo: 3, al_dia: 4, omitido: 5,
};

const ESTADO_LABEL: Record<string, string> = {
  al_dia: 'Al día',
  proximo: 'Próximo a vencer',
  vencido: 'Vencido',
  sin_iniciar: 'Sin iniciar',
  requiere_aclaracion: 'Requiere aclaración',
  omitido: 'Sin mantenimiento',
};

const estadoBadgeClass = (estado: string) => {
  switch (estado) {
    case 'vencido': return 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30';
    case 'proximo': return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30';
    case 'al_dia': return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30';
    case 'requiere_aclaracion': return 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

const fmt = (d?: Date) => (d ? format(d, 'MMM yyyy', { locale: es }) : '—');
const fmtDia = (iso?: string | null) =>
  (iso ? format(new Date(iso), "d MMM yyyy", { locale: es }) : 'sin fecha');

export const RevisionesResumen: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { rows, resumen, tiposDisponibles, loading, refetch, omitirCobertura } = useMantenimientosDashboard();
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState<string>(() => localStorage.getItem(LS_ESTADO) || 'todos');
  const [tipo, setTipo] = useState<string>(() => localStorage.getItem(LS_TIPO) || 'todos');
  const [rango, setRango] = useState<string>(() => localStorage.getItem(LS_RANGO) || '30');
  const [agrupar, setAgrupar] = useState<boolean>(() => localStorage.getItem(LS_AGRUPAR) === '1');
  const [seleccion, setSeleccion] = useState<string[]>([]);
  const [dialogoSistemas, setDialogoSistemas] = useState<SistemaRevisionRow[]>([]);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [fijarRow, setFijarRow] = useState<SistemaRevisionRow | null>(null);


  const cambiarEstado = (v: string) => { setEstado(v); localStorage.setItem(LS_ESTADO, v); setSeleccion([]); };
  const cambiarTipo = (v: string) => { setTipo(v); localStorage.setItem(LS_TIPO, v); };
  const cambiarRango = (v: string) => { setRango(v); localStorage.setItem(LS_RANGO, v); };
  const cambiarAgrupar = (v: boolean) => { setAgrupar(v); localStorage.setItem(LS_AGRUPAR, v ? '1' : '0'); };

  const dias = parseInt(rango, 10) || 30;

  const estadoEfectivo = (r: SistemaRevisionRow): string => {
    if (r.omitido) return 'omitido';
    const e = r.cobertura.estado;
    if (e === 'al_dia' && r.cobertura.diasDesfase >= -dias) return 'proximo';
    return e;
  };

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return rows
      .filter(r => {
        if (estado === 'omitido') return r.omitido;
        if (r.omitido) return false;
        if (estado === 'sin_programar') return !r.ot;
        if (estado === 'programadas') return !!r.ot;
        if (estado === 'sin_fijar') return r.cobertura.estado === 'sin_iniciar' && !r.anclaPeriodo;
        return estado === 'todos' || estadoEfectivo(r) === estado;
      })
      .filter(r => tipo === 'todos' || r.tipo === tipo)
      .filter(r => {
        if (!q) return true;
        return [r.clienteNombre, r.sedeNombre, r.sedePoblacion, r.sedeDireccion, r.ubicacion,
          getSystemTypeLabel(r.tipo), r.ot?.numero_ot]
          .some(v => (v || '').toLowerCase().includes(q));
      })
      .sort((a, b) => {
        if (agrupar) {
          const c = a.clienteNombre.localeCompare(b.clienteNombre);
          if (c !== 0) return c;
        }
        const d = (ORDEN[estadoEfectivo(a)] ?? 9) - (ORDEN[estadoEfectivo(b)] ?? 9);
        if (d !== 0) return d;
        return b.cobertura.diasDesfase - a.cobertura.diasDesfase;
      });
  }, [rows, estado, tipo, busqueda, dias, agrupar]);

  const seleccionadas = useMemo(
    () => filtradas.filter(r => seleccion.includes(r.sistemaId)),
    [filtradas, seleccion],
  );
  const clientesSeleccionados = useMemo(
    () => Array.from(new Set(seleccionadas.map(r => r.clienteId))),
    [seleccionadas],
  );

  const proximosRango = useMemo(
    () => rows.filter(r => !r.omitido && estadoEfectivo(r) === 'proximo').length,
    [rows, dias],
  );

  const sinProgramar = useMemo(
    () => rows.filter(r => !r.omitido && !r.ot && ['vencido', 'proximo', 'sin_iniciar'].includes(estadoEfectivo(r))).length,
    [rows, dias],
  );

  const tarjetas = [
    { key: 'vencido', label: 'Vencidos', value: resumen.vencido, icon: ShieldAlert, color: 'text-red-500' },
    { key: 'proximo', label: `Próximos (${dias} días)`, value: proximosRango, icon: Clock, color: 'text-amber-500' },
    { key: 'sin_programar', label: 'Sin programar', value: sinProgramar, icon: CalendarClock, color: 'text-orange-500' },
    { key: 'programadas', label: 'Programadas', value: resumen.programadas, icon: ClipboardCheck, color: 'text-blue-500' },
    { key: 'al_dia', label: 'Al día', value: resumen.al_dia, icon: ShieldCheck, color: 'text-emerald-500' },
    { key: 'sin_iniciar', label: 'Sin iniciar', value: resumen.sin_iniciar, icon: ShieldQuestion, color: 'text-muted-foreground' },
    { key: 'requiere_aclaracion', label: 'Por aclarar', value: resumen.requiere_aclaracion, icon: HelpCircle, color: 'text-purple-500' },
    { key: 'omitido', label: 'Omitidos', value: resumen.omitido, icon: EyeOff, color: 'text-muted-foreground' },
  ];

  const abrirProgramar = (sistemas: SistemaRevisionRow[]) => {
    setDialogoSistemas(sistemas);
    setDialogoAbierto(true);
  };

  const irAChecklist = (r: SistemaRevisionRow) => {
    const params = new URLSearchParams({
      sistemaId: r.sistemaId,
      sistemaTipo: r.tipo,
      clienteId: r.clienteId,
      clienteNombre: r.clienteNombre,
      returnTo: '/mantenimientos',
    });
    navigate(`/checklist-revision?${params.toString()}`);
  };

  const alternarOmitido = async (r: SistemaRevisionRow) => {
    try {
      await omitirCobertura(r.sistemaId, !r.omitido);
      toast({
        title: r.omitido ? 'Mantenimiento reactivado' : 'Mantenimiento omitido',
        description: `${r.clienteNombre} · ${getSystemTypeLabel(r.tipo)}`,
      });
    } catch {
      toast({ title: 'Error', description: 'No se pudo actualizar el sistema.', variant: 'destructive' });
    }
  };

  const exportar = () => {
    const data = filtradas.map(r => ({
      Cliente: r.clienteNombre,
      Sede: r.sedeNombre || '',
      Población: r.sedePoblacion || '',
      Sistema: getSystemTypeLabel(r.tipo),
      Estado: ESTADO_LABEL[estadoEfectivo(r)] ?? estadoEfectivo(r),
      'Última revisión': fmt(r.cobertura.ultima?.fecha),
      'Próxima revisión': fmt(r.cobertura.proxima?.fecha),
      'Tipo próxima': r.cobertura.proxima ? tipoProximaLabel(r.cobertura.proxima.tipo) : '',
      'Días de desfase': r.cobertura.diasDesfase,
      'OT programada': r.ot ? `${r.ot.numero_ot} (${fmtDia(r.ot.fecha_limite)})` : '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Mantenimientos');
    XLSX.writeFile(wb, `mantenimientos-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const desfaseTexto = (r: SistemaRevisionRow) => {
    const d = r.cobertura.diasDesfase;
    if (r.omitido) return 'Mantenimiento omitido';
    if (r.cobertura.estado === 'sin_iniciar') return 'Sin revisiones registradas';
    if (r.cobertura.estado === 'requiere_aclaracion') return 'Falta indicar el trimestre';
    if (d > 0) return `Vencida hace ${d} día${d === 1 ? '' : 's'}`;
    return `Faltan ${Math.abs(d)} día${Math.abs(d) === 1 ? '' : 's'}`;
  };

  let clienteAnterior = '';

  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {tarjetas.map(t => {
          const Icon = t.icon;
          const activo = estado === t.key;
          return (
            <Card
              key={t.key}
              onClick={() => cambiarEstado(activo ? 'todos' : t.key)}
              className={`cursor-pointer transition-shadow hover:shadow-md ${activo ? 'ring-2 ring-primary' : ''}`}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium">{t.label}</CardTitle>
                <Icon className={`h-4 w-4 ${t.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{t.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por cliente, sede, población o nº de OT..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>
        <Select value={estado} onValueChange={cambiarEstado}>
          <SelectTrigger className="md:w-52"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {Object.entries(ESTADO_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{k === 'omitido' ? 'Omitidos' : v}</SelectItem>
            ))}
            <SelectItem value="sin_programar">Sin programar</SelectItem>
            <SelectItem value="programadas">Con OT programada</SelectItem>
            <SelectItem value="sin_fijar">Sin iniciar y sin fecha fijada</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tipo} onValueChange={cambiarTipo}>
          <SelectTrigger className="md:w-56"><SelectValue placeholder="Tipo de sistema" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los sistemas</SelectItem>
            {tiposDisponibles.map(t => (
              <SelectItem key={t} value={t}>{getSystemTypeLabel(t)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={rango} onValueChange={cambiarRango}>
          <SelectTrigger className="md:w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Aviso 30 días</SelectItem>
            <SelectItem value="60">Aviso 60 días</SelectItem>
            <SelectItem value="90">Aviso 90 días</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Switch id="agrupar" checked={agrupar} onCheckedChange={cambiarAgrupar} />
          <Label htmlFor="agrupar" className="text-sm">Agrupar por cliente</Label>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={exportar}>
          <Download className="h-4 w-4" /> Exportar a Excel
        </Button>
      </div>

      {seleccion.length > 0 && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="flex flex-wrap items-center gap-3 py-3">
            <span className="text-sm font-medium">
              {seleccion.length} sistema{seleccion.length === 1 ? '' : 's'} seleccionado{seleccion.length === 1 ? '' : 's'}
            </span>
            <Button
              size="sm"
              className="gap-2"
              disabled={clientesSeleccionados.length !== 1}
              onClick={() => abrirProgramar(seleccionadas)}
            >
              <CalendarClock className="h-4 w-4" /> Programar en una sola OT
            </Button>
            {clientesSeleccionados.length > 1 && (
              <span className="text-xs text-muted-foreground">
                Has seleccionado sistemas de varios clientes: selecciona sólo los de un cliente para agruparlos en una OT.
              </span>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSeleccion([])}>Cancelar</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {filtradas.length} sistema{filtradas.length === 1 ? '' : 's'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {filtradas.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No hay sistemas que coincidan con los filtros.
            </p>
          )}
          {filtradas.map(r => {
            const Icon = getSystemTypeIcon(r.tipo);
            const ef = estadoEfectivo(r);
            const mostrarCabecera = agrupar && r.clienteNombre !== clienteAnterior;
            if (mostrarCabecera) clienteAnterior = r.clienteNombre;
            return (
              <React.Fragment key={r.sistemaId}>
                {mostrarCabecera && (
                  <div className="pt-3 pb-1 text-sm font-semibold text-muted-foreground">
                    {r.clienteNombre}
                  </div>
                )}
                <div className="flex items-start gap-2 rounded-lg border p-3 hover:bg-accent/40 transition-colors">
                  <Checkbox
                    className="mt-1 shrink-0"
                    checked={seleccion.includes(r.sistemaId)}
                    onCheckedChange={(c) => setSeleccion(prev =>
                      c ? [...prev, r.sistemaId] : prev.filter(id => id !== r.sistemaId))}
                    aria-label="Seleccionar sistema"
                  />
                  <button
                    onClick={() => navigate(`/cliente/${r.clienteId}`)}
                    className="flex-1 min-w-0 text-left min-h-[44px]"
                  >
                    <div className="flex flex-wrap items-center gap-2 justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="font-medium truncate">{r.clienteNombre}</span>
                        <Badge variant="outline" className="shrink-0">{getSystemTypeLabel(r.tipo)}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.anclaPeriodo && (
                          <Badge variant="outline" className="shrink-0 border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-400">
                            Periodo fijado
                          </Badge>
                        )}
                        <Badge variant="outline" className={estadoBadgeClass(ef)}>
                          {ESTADO_LABEL[ef] ?? ef}
                        </Badge>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {(r.sedeNombre || r.sedePoblacion) && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {[r.sedeNombre, r.sedePoblacion].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      <span>Última: {fmt(r.cobertura.ultima?.fecha)}</span>
                      <span>
                        Próxima: {fmt(r.cobertura.proxima?.fecha)}
                        {r.cobertura.proxima ? ` (${tipoProximaLabel(r.cobertura.proxima.tipo)})` : ''}
                      </span>
                      <span className={r.cobertura.diasDesfase > 0 && !r.omitido ? 'text-red-500 font-medium' : ''}>
                        {desfaseTexto(r)}
                      </span>
                    </div>
                    {r.ot && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/asignacion-tareas?otId=${r.ot!.id}`); }}
                        className="mt-2 inline-flex items-center gap-1 rounded-md border border-blue-500/30 bg-blue-500/10 px-2 py-1 text-xs text-blue-600 dark:text-blue-400"
                      >
                        <CalendarClock className="h-3 w-3" />
                        OT {r.ot.numero_ot} · {fmtDia(r.ot.fecha_limite)}
                        {r.ot.tecnicoNombre && (
                          <span className="inline-flex items-center gap-1">
                            <User className="h-3 w-3" />{r.ot.tecnicoNombre}
                          </span>
                        )}
                      </button>
                    )}
                  </button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="shrink-0 min-h-[44px] min-w-[44px]">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => abrirProgramar([r])} disabled={r.omitido}>
                        <CalendarClock className="h-4 w-4 mr-2" /> Programar revisión (OT)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => irAChecklist(r)} disabled={r.omitido}>
                        <ClipboardCheck className="h-4 w-4 mr-2" /> Empezar revisión ahora
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setFijarRow(r)}>
                        <CalendarCog className="h-4 w-4 mr-2" />
                        {r.anclaPeriodo ? 'Editar periodo fijado' : 'Fijar próxima revisión'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => alternarOmitido(r)}>
                        {r.omitido
                          ? (<><Eye className="h-4 w-4 mr-2" /> Reactivar mantenimiento</>)
                          : (<><EyeOff className="h-4 w-4 mr-2" /> Omitir mantenimiento</>)}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/cliente/${r.clienteId}`)}>
                        <FileSpreadsheet className="h-4 w-4 mr-2" /> Ver ficha del cliente
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </React.Fragment>
            );
          })}
        </CardContent>
      </Card>

      <ProgramarRevisionDialog
        open={dialogoAbierto}
        onOpenChange={setDialogoAbierto}
        sistemas={dialogoSistemas}
        onCreada={() => { setSeleccion([]); refetch(); }}
      />

      {fijarRow && (
        <FijarPeriodoRevisionDialog
          open={!!fijarRow}
          onOpenChange={(v) => { if (!v) setFijarRow(null); }}
          sistemaId={fijarRow.sistemaId}
          sistemaTipo={fijarRow.tipo}
          descripcion={fijarRow.sedeNombre ?? fijarRow.ubicacion ?? undefined}
          anclaPeriodo={fijarRow.anclaPeriodo}
          anclaTipo={fijarRow.anclaTipo}
          onGuardado={refetch}
        />
      )}
    </div>
  );
};
